<template>
  <div id="assetsListToolbar" class="flex h-16 border-b bg-gray-50">
    <!-- TOGGLE PAGE SPLITTER BUTTON -->
    <div class="flex flex-col pl-1 m-auto w-11">
      <!-- <font-awesome-icon
        icon="fa-light fa-bars-sort" class="w-4 h-4 mt-1 cursor-pointer hover:text-primary-500"
        :class="{ 'transform scale-x-[-1]': !asideOpen }"
        @click="$emit('asideClick')"
      /> -->
    </div>

    <!-- HEADER INFO -->
    <div class="pt-2 mr-2 overflow-x-auto overflow-y-hidden grow">
      <template v-if="getHeader.type === 'search'">
        <div class="w-full h-8 pt-1 text-lg font-semibold text-default">
          {{ t('general.digitalAssets') }}
        </div>
        <div class="w-full h-4 text-xs leading-3 tracking-wide">
          {{ getHeader.searchText === '*' ? t('assetsList.allAssets') : getHeader.searchText }}
        </div>
      </template>
      <template v-else-if="getHeader.type === 'filter'">
        <div class="flex flex-row w-full h-5 pt-1 text-xs leading-3 tracking-wide">
          <div class="pr-5 text-lg leading-4 uppercase" v-text="t('general.digitalAssets')" />
          <tx-button type="text" class="text-primary-500" :text="t('general.reset')" @click="doClearFilter()" />
        </div>
        <div class="flex flex-row w-full h-10 pt-2">
          <tx-tag
            v-for="filter in getHeader.filters" :key="filter" clickable
            @click="openFilter(filter)"
          >
            {{ filter }}
          </tx-tag>
        </div>
      </template>
      <template v-if="getHeader.type === 'changeAccount'">
        <div class="w-full h-8 pt-1 text-lg font-semibold text-default">
          {{ t('general.digitalAssets') }}
        </div>
        <div class="w-full h-4 text-xs leading-3 tracking-wide">
          {{ getHeader.searchText === '*' ? t('assetsList.allAssets') : getHeader.searchText }}
        </div>
      </template>
    </div>

    <!-- SELECT ALL BUTTON -->
    <div class="w-24 m-auto">
      <tx-checkbox
        id="selectAll" v-model="getSetSelectAll" :indeterminate="selectAllIndeterminate"
        :label="t('general.selectAll')"
      />
    </div>

    <!-- CAHNGE ACCOUNT BUTTON -->
    <div v-if="showChangeAccount" class="m-auto cursor-pointer" @click="changeAccount">
      <div class="pt-1 text-m font-semibold">
        {{ t('digitalAssets.currentAccount') }}
      </div>
      <div class="flex items-center space-x-1">
        <span>{{ getCurrentAccount }}</span>
        <font-awesome-icon
          icon="fa-light fa-angle-down"
          class="w-4 h-4 text-default"
        />
      </div>
    </div>

    <!-- SORT BUTTON -->
    <div class="m-auto w-11">
      <!-- <tx-dropdown
        v-model="getSetSortBy" :items="getSortByAttributes" value-prop="articleProperty"
        display-prop="displayLabel" faicon="fa-light fa-sort" :tooltip="t('general.sort')" @select="onSortSelectionChange"
      /> -->
    </div>

    <!-- SEARCH BUTTON -->
    <div class="m-auto w-11">
      <tx-button
        id="btnSearch" v-tooltip="{ text: t('general.search'), theme: { offset: ['right'] } }" type="icon" faicon="fa-light fa-search"
        @click="openSearch"
      />
    </div>

    <!-- REVERSE LOOKUP BUTTON -->
    <div class="m-auto w-11">
      <tx-button
        id="btnReverse" v-tooltip="{ text: t('assetsList.reverseLookup'), theme: { offset: ['right'] } }" type="icon" faicon="fa-light fa-magnifying-glass-arrows-rotate"
        @click="openReverseLookup"
      />
    </div>

    <!-- FILTER BUTTON -->
    <div class="m-auto w-11">
      <tx-button
        id="btnFilter" v-tooltip="t('general.filter')" type="icon" faicon="fa-light fa-filter"
        @click="openFilter"
      />
    </div>

    <!-- THUMBNAIL SIZE BUTTON -->
    <div class="m-auto w-11">
      <tx-button
        id="btnThumbSize" v-tooltip="{ text: t('general.thumbnailSize'), theme: { offset: ['right'] } }" type="icon" faicon="fa-light fa-gallery-thumbnails"
        @click="doSwitchThumbSize"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useUserStore } from '@/store/userData'
import TxButton from '@/shared/components/TxButton.vue'
import TxCheckbox from '@/shared/components/TxCheckbox.vue'
import TxTag from '@/shared/components/TxTag.vue'

interface IProps {
  asideOpen: boolean
  selectAll: boolean
  destination: IAssetsToolbarHeader
  selectAllIndeterminate: boolean
  isAnyDataAvailable: boolean
  isAIAssisted: boolean
}
const props = withDefaults(defineProps<IProps>(), {
  isAnyDataAvailable: false,
})

const emit = defineEmits<{
  (e: 'asideClick'): void
  (e: 'clearAllFilters', filter?: any): void
  (e: 'clearFilter', filter?: any): void
  (e: 'filter', filter?: any): void
  (e: 'search'): void
  (e: 'selectAllChange', value: boolean): void
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:selectAll', value: boolean): void
  (e: 'openCustomSort', value: boolean): void
  (e: 'reverseLookup'): void
  (e: 'changeAccount'): void
}>()

const { t } = useI18n()
const userStore = useUserStore()

const getHeader = computed(() => {
  switch (props.destination.type) {
    case 'search':
      return { type: 'search', searchText: props.destination.searchText || '*' }
    case 'filter':
      return {
        type: 'filter',
        filters: props.destination.filters || [],
      }
    default:
      return { type: '' }
  }
})

const getSetSelectAll = computed({
  get: () => props.selectAll,
  set: (val) => {
    emit('update:selectAll', val)
    emit('selectAllChange', val)
  },
})

const getCurrentAccount = computed(() => {
  return userStore.sellerDetails?.AccountName
})

const showChangeAccount = computed(() => {
  return userStore.userProfile?.AccountDetails?.AccountTypeId === 1 && !userStore.activeCatalog && userStore.sellerDetails?.AccountName
})

function doSwitchThumbSize() {
  const newSize = userStore.thumbnailSize <= 1 ? 3 : userStore.thumbnailSize - 1
  userStore.setThumbnailSize(newSize)
}

function doClearFilter(filter?: any) {
  if (!filter) {
    emit('clearAllFilters', filter)
  }
  else {
    emit('clearFilter', filter)
  }
}

function openFilter(filter?: any) {
  if (filter) {
    emit('filter', filter)
  }
}

function openSearch() {
  emit('search')
}

function openReverseLookup() {
  emit('reverseLookup')
}

function changeAccount() {
  emit('changeAccount')
}
</script>
